import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to detect when a user has been idle.
 * @param {Function} onIdle - The function to call when the user is detected as idle.
 * @param {number} idleTimeout - The timeout in milliseconds. Defaults to 3 hours.
 */
export const useIdleTimer = (onIdle, idleTimeout = 3 * 60 * 60 * 1000) => {
  const [isIdle, setIsIdle] = useState(false);
  const timeoutIdRef = useRef(null);

  const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];

  const resetTimer = () => {
    // Clear the previous timeout
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    
    // Set a new timeout
    timeoutIdRef.current = setTimeout(() => {
      setIsIdle(true);
      if (onIdle) {
        onIdle();
      }
    }, idleTimeout);
  };

  useEffect(() => {
    // Set the initial timer
    resetTimer();

    // Set up event listeners to reset the timer on user activity
    const handleActivity = () => {
      // If we are currently idle, we don't need to do anything here
      // The user will be logged out. They need to log back in.
      // If you wanted to "wake up" the session, you'd call resetTimer() here.
      // For a security timeout, it's better to let the onIdle action (logout) be final.
      resetTimer();
    };

    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Cleanup function to remove event listeners and clear the timeout
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idleTimeout, onIdle]); // Re-run effect if timeout or onIdle function changes

  return isIdle;
};
